import React, { useState } from 'react';
import { analysisApi } from '../services/api';
import { generateAnalysisReport } from '../utils/pdfExportUtils';
import { generateAnalysisReportV2 } from '../utils/pdfExportUtilsV2';

interface BulkJobExportProps {
  bulkJobId: string;
  promptName?: string;
  className?: string;
  /**
   * 'buttons' (default) — the original standalone v1/v2 button pair.
   * 'menu-items' — rows for the consolidated Export dropdown (deepcontext-c7ac).
   * 'icon' — a single compact outline action for the detail rail header.
   */
  renderAs?: 'buttons' | 'menu-items' | 'icon';
  /** Called when an export is kicked off from inside a dropdown, so the parent can close the menu. */
  onMenuAction?: () => void;
}

const BulkJobExport: React.FC<BulkJobExportProps> = ({ bulkJobId, promptName, className = '', renderAs = 'buttons', onMenuAction }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingV2, setIsExportingV2] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runExport = async (variant: 'v1' | 'v2') => {
    onMenuAction?.();
    if (variant === 'v2') setIsExportingV2(true); else setIsExporting(true);
    setError(null);

    try {
      // Fetch bulk job details
      const response = await analysisApi.getBulkJobDetails(bulkJobId);

      if (!response.success) {
        throw new Error('Failed to fetch bulk job details');
      }

      const reportData = {
        bulkJob: {
          id: response.bulkJob.id,
          promptId: response.bulkJob.promptId,
          promptName: response.bulkJob.promptName,
          createdAt: response.bulkJob.analyzedAt,
          totalUrls: response.bulkJob.totalUrls,
          matchedUrls: response.bulkJob.matchedUrls,
          unmatchedUrls: response.bulkJob.unmatchedUrls,
          averageConfidence: response.bulkJob.averageConfidence,
          totalCost: response.bulkJob.totalCost
        },
        prompt: {
          name: response.bulkJob.promptName,
          description: response.bulkJob.promptDescription,
          prompt: response.bulkJob.promptContent,
          type: response.bulkJob.promptType
        },
        matchedSamples: response.matchedSamples.map(sample => ({
          url: sample.url,
          reasoning: sample.reasoning,
          confidence: sample.confidence,
          contentSummary: sample.contentSummary
        })),
        unmatchedSamples: response.unmatchedSamples.map(sample => ({
          url: sample.url,
          reasoning: sample.reasoning,
          confidence: sample.confidence,
          contentSummary: sample.contentSummary
        }))
      };

      if (variant === 'v2') {
        await generateAnalysisReportV2(reportData);
      } else {
        await generateAnalysisReport(reportData);
      }

    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
      setIsExportingV2(false);
    }
  };

  const handleExport = () => runExport('v1');
  const handleExportV2 = () => runExport('v2');
  const busy = isExporting || isExportingV2;

  if (renderAs === 'icon') {
    return (
      <div className={className} data-testid="rail-pdf-export">
        <button
          onClick={handleExport}
          disabled={busy}
          title="Export analysis report as PDF"
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-accent-active text-accent-active text-xs font-bold hover:bg-ghost-bg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <span className="inline-block w-3 h-3 border-2 border-accent-active border-t-transparent rounded-full animate-spin" />
          ) : (
            'PDF'
          )}
        </button>
        {error && <div className="mt-2 text-xs text-risk">{error}</div>}
      </div>
    );
  }

  if (renderAs === 'menu-items') {
    return (
      <div className={className}>
        <div className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-ink-muted">This Analysis</div>
        <button
          onClick={handleExport}
          disabled={busy}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-ink hover:bg-ghost-bg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting && <span className="inline-block w-3 h-3 border-2 border-ink-muted border-t-transparent rounded-full animate-spin" />}
          This Analysis (PDF)
        </button>
        <button
          onClick={handleExportV2}
          disabled={busy}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-ink hover:bg-ghost-bg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExportingV2 && <span className="inline-block w-3 h-3 border-2 border-ink-muted border-t-transparent rounded-full animate-spin" />}
          This Analysis (PDF v2)
        </button>
        {error && <div className="px-4 py-1 text-xs text-risk">{error}</div>}
      </div>
    );
  }

  return (
    <>
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <button
          onClick={handleExport}
          disabled={isExporting || isExportingV2}
          className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm leading-4 font-medium rounded-md text-ink bg-surface hover:bg-ghost-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Export analysis report as PDF"
        >
          {isExporting ? (
            <>
              <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-ink" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export PDF
            </>
          )}
        </button>

        <button
          onClick={handleExportV2}
          disabled={isExporting || isExportingV2}
          className="inline-flex items-center px-3 py-2 border border-primary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Export analysis report as PDF (V2 — Brand-Forward layout, beta)"
        >
          {isExportingV2 ? (
            <>
              <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-primary-700" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export PDF 2
            </>
          )}
        </button>
      </div>
      {error && (
        <div className="mt-2 text-sm text-risk">
          {error}
        </div>
      )}
    </>
  );
};

export default BulkJobExport;